// File: Indexer.jsx

import React from "react";
import PropTypes from "prop-types";
import { head, includes, trim, template, values } from "lodash";

import LoadingState from "@/components/items-list/components/LoadingState";
import routeWithUserSession from "@/components/application-area/routeWithUserSession";
import navigateTo from "@/components/application-area/navigateTo";

import { currentUser } from "@/services/auth";
import notification from "@/services/notification";
import IndexerService from "@/services/indexer";

import { Query as QueryService } from "@/services/query";
import routes from "@/services/routes";

import MenuButton from "../../components/indexers/MenuButton";
import IndexerView from "../../components/indexers/IndexerView";
import IndexerEdit from "../../components/indexers/IndexerEdit";
import IndexerNew from "../../components/indexers/IndexerNew";

const MODES = {
  NEW: 0,
  VIEW: 1,
  EDIT: 2
};

const defaultNameBuilder = template("<%= query.name %>: <%= options.column %> <%= options.op %> <%= options.value %>");

export function getDefaultName(indexer) {
  if (!indexer.query) {
    return "New Indexer";
  }
  return defaultNameBuilder(indexer);
}

class Indexer extends React.Component {
  static propTypes = {
    mode: PropTypes.oneOf(values(MODES)),
    indexerId: PropTypes.string,
    onError: PropTypes.func,
  };

  static defaultProps = {
    mode: null,
    indexerId: null,
    onError: () => {},
  };

  _isMounted = false;

  state = {
    indexer: null,
    queryResult: null,
    canEdit: false,
    mode: null,
    events: null,
  };

  componentDidMount() {
    this._isMounted = true;
    const { mode, indexerId } = this.props;
    this.setState({ mode });

    if (mode === MODES.NEW) {
      this.setState({
        indexer: {
          options: {
            op: ">",
            value: 1,
            muted: false,
          },
          },
        canEdit: true,
        is_archived: false,
        is_favorite: false,
        tags: [],
      });
    } else {
      IndexerService.get({ id: indexerId }) // Use indexerId to fetch indexer details
        .then(indexer => {
          if (this._isMounted) {
            const canEdit = currentUser.canEdit(indexer);

            // force view mode if can't edit
            if (!canEdit) {
              this.setState({ mode: MODES.VIEW });
              notification.warn(
                "You cannot edit this indexer",
                "You do not have sufficient permissions to edit this indexer, and have been redirected to the view-only page.",
                { duration: 0 }
              );
            }

            this.setState({ indexer, canEdit, is_favorite: indexer.is_favorite , tags: indexer.tags});
            this.onQuerySelected(indexer.query);

          }
        })
        .catch(error => {
          if (this._isMounted) {
            this.props.onError(error);
          }
        });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }


  save = () => {
    const { indexer } = this.state;
    indexer.name = trim(indexer.name) || getDefaultName(indexer);

    // Ensure data_source is properly set from options if it exists
    if (indexer.options && indexer.options.data_source_id && !indexer.data_source) {
      indexer.data_source = { id: indexer.options.data_source_id };
    }

    return IndexerService.save(indexer)
      .then(indexer => {
        notification.success("Saved.");
        this.setState({ indexer, mode: MODES.VIEW });
        navigateTo(`indexers/${indexer.id}`, true);
      })
      .catch(() => {
        notification.error("Failed saving indexer.");
      });
  };


  onQuerySelected = query => {
    this.setState(({ indexer }) => ({
      indexer: Object.assign(indexer, { query }),
      queryResult: null, 
    }));

    if (query) {
      // get cached result for column names and values
      new QueryService(query).getQueryResultPromise().then(queryResult => {
        if (this._isMounted) {
          this.setState({ queryResult });
          let { column } = this.state.indexer.options;
          const columns = queryResult.getColumnNames();

          // default to first column name if none chosen, or irrelevant in current query
          if (!column || !includes(columns, column)) {
            column = head(queryResult.getColumnNames());
          }
          this.setIndexerOptions({ column });
        }
      });
    }
  };

  onNameChange = name => {
    const { indexer } = this.state;
    this.setState({
      indexer: Object.assign(indexer, { name }),
    });
  };

  setIndexerOptions = (obj) => {
    const { indexer } = this.state;
    const options = { ...indexer.options, ...obj };
    
    // If data_source_id is being set, also update the data_source object
    if (obj.data_source_id !== undefined) {
      this.setState({
        indexer: Object.assign(indexer, { 
          options,
          data_source: obj.data_source_id ? { id: obj.data_source_id } : null
        }),
      });
    } else {
      this.setState({
        indexer: Object.assign(indexer, { options }),
      });
    }
  };


  delete = () => {
    const { indexer } = this.state;
    return IndexerService.delete(indexer.id)
      .then(() => {
        notification.success("Indexer deleted successfully.");
        navigateTo("indexers");
      })
      .catch(() => {
        notification.error("Failed deleting indexer.");
      });
  };

  archive = () => {
    const { indexer } = this.state;
    indexer.is_archived = true;
    return IndexerService.save(indexer)
      .then(() => {
        this.setState({ indexer });
        notification.success("Indexer archived successfully.");
        navigateTo(`indexers/archive`);
      })
      .catch(() => {
        notification.error("Failed archiving indexer.");
      });
  };

  unarchive = () => {
    const { indexer } = this.state;
    indexer.is_archived = false;
    return IndexerService.save(indexer)
      .then(() => {
        this.setState({ indexer });
        notification.success("Indexer unarchived successfully.");
        navigateTo(`indexers/${indexer.id}/edit`, true);
        this.setState({ mode: MODES.EDIT});
      })
      .catch(() => {
        notification.error("Failed unarchiving indexer.");
      });
  };

  mute = () => {
    const { indexer } = this.state;
    return IndexerService.mute(indexer)
      .then(() => {
        this.setIndexerOptions({ muted: true });
        notification.warn("Notifications have been muted.");
      })
      .catch(() => {
        notification.error("Failed muting notifications.");
      });
  };

  unmute = () => {
    const { indexer } = this.state;
    return IndexerService.unmute(indexer)
      .then(() => {
        this.setIndexerOptions({ muted: false });
        notification.success("Notifications have been restored.");
      })
      .catch(() => {
        notification.error("Failed restoring notifications.");
      });
  };

  edit = () => {
    const { id } = this.state.indexer;
    navigateTo(`indexers/${id}/edit`, true);
    this.setState({ mode: MODES.EDIT });
  };

  cancel = () => {
    const { id } = this.state.indexer;
    navigateTo(`indexers/${id}/view`, true);
    this.setState({ mode: MODES.VIEW});
  };

  favorite = () => {
    const { id } = this.state.indexer;
    return IndexerService.favorite(id)
      .then(() => {
        notification.success("Indexer added to favorites.");
      })
      .catch(() => {
        notification.error("Failed adding indexer to favorites.");
      });
  };

  unfavorite = () => {
    const { id } = this.state.indexer;
    return IndexerService.unfavorite(id)
      .then(() => {
        //this.unfavorite()
        notification.success("Indexer removed from favorites.");
      })
      .catch(() => {
        notification.error("Failed removing indexer from favorites.");
      });
  };

  setIndexerTags = obj => {
    if (!Array.isArray(obj)) {
        console.error("Expected an array for tags, but received:", obj);
        return;
    }

    this.setState(prevState => ({
        indexer: { ...prevState.indexer, tags: obj }
    }), this.save); // Ensure save is called after state update
};

  render() {
    const { indexer } = this.state;
    if (!indexer) {
      return <LoadingState className="m-t-30" />;
    }

    const muted = !!indexer.options.muted;
    const { queryResult, mode, canEdit, pendingRearm } = this.state;

    const menuButton = (
      <MenuButton 
        doDelete={this.delete} 
        muted={muted} 
        mute={this.mute} 
        unmute={this.unmute} 
        canEdit={canEdit} 
        doArchive={this.archive} 
        doUnarchive={this.unarchive} 
        archived={indexer.is_archived} 
        />
    );
    
    const enhancedIndexer = {
      ...indexer,
      favorite: this.favorite,
      unfavorite: this.unfavorite,
      is_favorite: indexer.is_favorite,
      tags: indexer.tags,
      query: indexer.query,
      rearm: indexer.rearm,
    };

    const commonProps = {
      indexer: enhancedIndexer,
      queryResult,
      pendingRearm,
      save: this.save,
      setIndexerTags: this.setIndexerTags,
      menuButton,
      onQuerySelected: this.onQuerySelected,
      onRearmChange: this.onRearmChange,
      onNameChange: this.onNameChange,
      onCriteriaChange: this.setIndexerOptions,
      onOptionsChange: this.setIndexerOptions,
      onNotificationTemplateChange: this.setIndexerOptions,
      onChange: () => {},
    };

    return (
      <div className="indexer-page">
        <div className="container">
          {mode === MODES.NEW && <IndexerNew {...commonProps} />}
          {mode === MODES.VIEW && (
            <IndexerView canEdit={canEdit} onEdit={this.edit} muted={muted} unmute={this.unmute} {...commonProps} />
          )}
          {mode === MODES.EDIT && <IndexerEdit cancel={this.cancel} {...commonProps} />}
          
        </div>
      </div>
    );
  }
}

routes.register(
  "Indexers.New",
  routeWithUserSession({
    path: "/indexers/new",
    title: "New Indexer",
    render: pageProps => <Indexer {...pageProps} mode={MODES.NEW} />,
  })
);
routes.register(
  "Indexers.View",
  routeWithUserSession({
    path: "/indexers/:indexerId",
    title: "Indexer",
    render: pageProps => <Indexer {...pageProps} mode={MODES.VIEW} />,
  })
);
routes.register(
  "Indexers.Edit",
  routeWithUserSession({
    path: "/indexers/:indexerId/edit",
    title: "Indexer",
    render: pageProps => <Indexer {...pageProps} mode={MODES.EDIT} />,
  })
);
